import {
  Box,
  Button,
  TextField,
  Typography,
  Select,
  MenuItem,
  FormControl,
  useTheme,
} from "@mui/material";
import { useState, useEffect } from "react";
import { tokens } from "../../../theme";
import type { EquipmentUnitFormData } from "../../../types/equipment";

interface UnitDetailsStepProps {
  formData: EquipmentUnitFormData;
  onChange: (field: keyof EquipmentUnitFormData, value: string) => void;
  onPrevious: () => void;
  onNext: () => void;
  onSave: () => void;
  forceSerialError?: boolean;
  onSerialErrorCleared?: () => void;
}

const STATUS_OPTIONS = [
  { value: "available",    label: "Available" },
  { value: "allocated",    label: "Allocated" },
  { value: "rented",       label: "Rented" },
  { value: "maintenance",  label: "Maintenance" },
  { value: "lost",         label: "Damaged / Lost" },
];

const CONDITION_OPTIONS = [
  { value: "new",     label: "New" },
  { value: "good",    label: "Good" },
  { value: "fair",    label: "Fair" },
  { value: "poor",    label: "Poor" },
  { value: "damaged", label: "Damaged" },
];

export default function UnitDetailsStep({
  formData,
  onChange,
  onPrevious,
  onNext,
  onSave,
  forceSerialError,
  onSerialErrorCleared,
}: UnitDetailsStepProps) {
  const theme = useTheme();
  const colors = tokens(theme.palette.mode);
  const [errors, setErrors] = useState<Record<string, string>>({});

  useEffect(() => {
    if (forceSerialError) {
      setErrors({ serial_number: "Serial number is required to save" });
    }
  }, [forceSerialError]);

  const inputSx = {
    "& .MuiOutlinedInput-root": {
      backgroundColor: colors.primary[800],
      borderRadius: "16px",
      "& fieldset": { border: "none" },
      "&:hover fieldset": { border: "none" },
      "&.Mui-focused fieldset": { border: "none" },
    },
  };

  const labelSx = {
    fontWeight: 600,
    color: colors.primary[400],
    mb: 0.5,
    fontSize: "0.88rem",
  };

  const errorSx = {
    fontSize: "0.75rem",
    color: "error.main",
    mt: 0.5,
  };

  const validate = () => {
    const newErrors: Record<string, string> = {};
    if (!formData.serial_number.trim()) newErrors.serial_number = "Serial number is required";
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleNext = () => {
    if (validate()) onNext();
  };

  const handleSerialChange = (val: string) => {
    onChange("serial_number", val);
    if (val.trim()) {
      setErrors((p) => ({ ...p, serial_number: "" }));
      onSerialErrorCleared?.();
    }
  };

  return (
    <Box sx={{ display: "flex", flexDirection: "column", gap: 2.5 }}>

      <Box sx={{ display: "grid", gridTemplateColumns: { xs: "1fr", sm: "1fr 1fr" }, gap: 2 }}>
        <Box>
          <Typography variant="body2" sx={labelSx}>Serial Number *</Typography>
          <TextField
            fullWidth
            placeholder="e.g. SN-1234"
            size="small"
            value={formData.serial_number}
            onChange={(e) => handleSerialChange(e.target.value)}
            error={!!errors.serial_number}
            sx={inputSx}
            autoFocus={!!forceSerialError}
          />
          {errors.serial_number && <Typography sx={errorSx}>{errors.serial_number}</Typography>}
        </Box>
        <Box>
          <Typography variant="body2" sx={labelSx}>Asset Tag</Typography>
          <TextField
            fullWidth
            placeholder="e.g. TAG-001"
            size="small"
            value={formData.asset_tag}
            onChange={(e) => onChange("asset_tag", e.target.value)}
            sx={inputSx}
          />
        </Box>
      </Box>

      <Box sx={{ display: "grid", gridTemplateColumns: { xs: "1fr", sm: "1fr 1fr" }, gap: 2 }}>
        {/* Status Select */}
        <Box>
          <Typography variant="body2" sx={labelSx}>Status</Typography>
          <FormControl fullWidth size="small" sx={inputSx}>
            <Select
              value={formData.status}
              onChange={(e) => onChange("status", e.target.value)}
            >
              {STATUS_OPTIONS.map((opt) => (
                <MenuItem key={opt.value} value={opt.value}>
                  {opt.label}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
        </Box>

        <Box>
          <Typography variant="body2" sx={labelSx}>Condition</Typography>
          <FormControl fullWidth size="small" sx={inputSx}>
            <Select
              value={formData.condition}
              onChange={(e) => onChange("condition", e.target.value)}
            >
              {CONDITION_OPTIONS.map((opt) => (
                <MenuItem key={opt.value} value={opt.value}>
                  {opt.label}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
        </Box>
      </Box>

      {/* Location */}
      <Box>
        <Typography variant="body2" sx={labelSx}>Location</Typography>
        <TextField
          fullWidth
          placeholder="e.g. Studio A, Shelf 3"
          size="small"
          value={formData.location}
          onChange={(e) => onChange("location", e.target.value)}
          sx={inputSx}
        />
      </Box>

      {/* Notes */}
      <Box>
        <Typography variant="body2" sx={labelSx}>Notes</Typography>
        <TextField
          fullWidth
          placeholder="Any additional notes about this unit..."
          size="small"
          multiline
          rows={2}
          value={formData.description}
          onChange={(e) => onChange("description", e.target.value)}
          sx={inputSx}
        />
      </Box>

      {/* Nav */}
      <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center", mt: 2 }}>
        <Box sx={{ display: "flex", gap: 3 }}>
          <Typography
            onClick={onPrevious}
            sx={{ cursor: "pointer", fontWeight: 600, textDecoration: "underline", color: colors.primary[400], fontSize: "0.9rem" }}
          >
            Previous
          </Typography>
          <Typography
            onClick={onSave}
            sx={{ cursor: "pointer", fontWeight: 600, textDecoration: "underline", color: colors.primary[400], fontSize: "0.9rem" }}
          >
            Save Gear
          </Typography>
        </Box>
        <Button
          variant="contained"
          size="large"
          onClick={handleNext}
          sx={{ backgroundColor: colors.primary[400], px: 5, py: 1.2, borderRadius: "24px" }}
        >
          Next
        </Button>
      </Box>
    </Box>
  );
}